import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Check, Clapperboard, Image as ImageIcon, Loader2, Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";

const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#0B0B0E] px-3 py-2.5 text-sm text-white placeholder-zinc-600 outline-none focus:border-emerald-300/60";
const smallInputCls =
  "w-full rounded-lg border border-white/10 bg-[#0B0B0E] px-2.5 py-1.5 text-xs text-white placeholder-zinc-600 outline-none focus:border-emerald-300/60";

const EMPTY_SERVICE = { title: "", price_label: "", description: "", image: "", hover_video: "", faq: [] };

function MediaUpload({ label, accept, value, onChange, testid, previewAs = "image" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => inputRef.current?.click();

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/media/upload", fd, { timeout: 180000 });
      onChange(data.url);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex items-center gap-3">
        {value ? (
          <div className="relative h-16 w-24 overflow-hidden rounded-lg border border-white/10 bg-[#0B0B0E]">
            {previewAs === "video" ? (
              <video src={fileUrl(value)} muted autoPlay loop playsInline className="h-full w-full object-cover" />
            ) : (
              <img src={fileUrl(value)} alt="" className="h-full w-full object-cover" />
            )}
            <button
              type="button"
              onClick={() => onChange("")}
              data-testid={`${testid}-clear`}
              aria-label="Remove"
              className="absolute right-1 top-1 rounded-full bg-black/70 p-1 text-red-300 hover:bg-red-500/30"
            >
              <X size={10} />
            </button>
          </div>
        ) : (
          <div className="flex h-16 w-24 items-center justify-center rounded-lg border border-dashed border-white/15 text-zinc-600">
            {previewAs === "video" ? <Clapperboard size={16} /> : <ImageIcon size={16} />}
          </div>
        )}
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={pick}
            disabled={busy}
            data-testid={`${testid}-btn`}
            className="flex items-center gap-1.5 rounded-full border border-emerald-300/40 bg-emerald-300/5 px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-200 hover:bg-emerald-300 hover:text-black disabled:opacity-50"
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
            {value ? `Replace ${label}` : `Upload ${label}`}
          </button>
          <input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="or paste a URL"
            className={smallInputCls}
            data-testid={`${testid}-url`}
          />
        </div>
      </div>
      <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={upload} />
    </div>
  );
}

function FaqEditor({ faq, onChange, testidPrefix }) {
  const add = () => onChange([...(faq || []), { question: "", answer: "" }]);
  const update = (i, patch) =>
    onChange((faq || []).map((item, idx) => (idx === i ? { ...item, ...patch } : item)));
  const remove = (i) => onChange((faq || []).filter((_, idx) => idx !== i));
  const move = (i, delta) => {
    const next = [...(faq || [])];
    const target = i + delta;
    if (target < 0 || target >= next.length) return;
    [next[i], next[target]] = [next[target], next[i]];
    onChange(next);
  };

  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-faq-list`}>
      {(faq || []).map((item, i) => (
        <div
          key={i}
          className="space-y-2 rounded-xl border border-white/10 bg-[#0B0B0E] p-3"
          data-testid={`${testidPrefix}-faq-item-${i}`}
        >
          <div className="flex items-start gap-2">
            <input
              value={item.question}
              onChange={(e) => update(i, { question: e.target.value })}
              placeholder="Question"
              className={smallInputCls}
              data-testid={`${testidPrefix}-faq-question-${i}`}
            />
            <div className="flex shrink-0 gap-1">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up"
                className="rounded-md border border-white/10 p-1.5 text-zinc-300 hover:border-emerald-300/40 hover:bg-emerald-300/10 disabled:cursor-not-allowed disabled:opacity-30">
                <ArrowUp size={11} />
              </button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === (faq || []).length - 1} aria-label="Move down"
                className="rounded-md border border-white/10 p-1.5 text-zinc-300 hover:border-emerald-300/40 hover:bg-emerald-300/10 disabled:cursor-not-allowed disabled:opacity-30">
                <ArrowDown size={11} />
              </button>
              <button type="button" onClick={() => remove(i)} data-testid={`${testidPrefix}-faq-remove-${i}`} aria-label="Remove FAQ"
                className="rounded-md border border-red-500/30 p-1.5 text-red-400 hover:bg-red-500/10">
                <X size={11} />
              </button>
            </div>
          </div>
          <textarea rows={2} value={item.answer} onChange={(e) => update(i, { answer: e.target.value })}
            placeholder="Answer" className={smallInputCls} data-testid={`${testidPrefix}-faq-answer-${i}`} />
        </div>
      ))}
      <button type="button" onClick={add} data-testid={`${testidPrefix}-faq-add`}
        className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-white/15 py-2 text-[11px] font-bold uppercase tracking-wider text-zinc-400 hover:border-emerald-300/40 hover:text-emerald-200">
        <Plus size={11} /> Add FAQ
      </button>
    </div>
  );
}

function ServiceForm({ initial, onSaved, onCancel, isNew }) {
  const [form, setForm] = useState(initial || EMPTY_SERVICE);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm(initial || EMPTY_SERVICE);
  }, [initial]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.price_label.trim() || !form.description.trim()) {
      return toast.error("Title, price label and description are required");
    }
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        price_label: form.price_label.trim(),
        description: form.description.trim(),
        image: (form.image || "").trim(),
        hover_video: (form.hover_video || "").trim(),
        sort: form.sort ?? 0,
        faq: (form.faq || [])
          .map((f) => ({ question: (f.question || "").trim(), answer: (f.answer || "").trim() }))
          .filter((f) => f.question && f.answer),
      };
      let saved;
      if (isNew) {
        const { data } = await api.post("/admin/services", payload);
        saved = data;
      } else {
        const { data } = await api.put(`/admin/services/${initial.id}`, payload);
        saved = data;
      }
      toast.success(isNew ? "Service created" : "Service saved");
      onSaved(saved);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const testidPrefix = isNew ? "admin-service-new" : `admin-service-edit-${initial?.id}`;

  return (
    <form
      onSubmit={submit}
      className="space-y-3 rounded-2xl border border-emerald-300/30 bg-[#141419] p-5"
      data-testid={`${testidPrefix}-form`}
    >
      <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
        placeholder="Service title" className={inputCls} data-testid={`${testidPrefix}-title`} />
      <input required value={form.price_label} onChange={(e) => setForm({ ...form, price_label: e.target.value })}
        placeholder="Price label (e.g. $40 / Track — $105 / 3 Tracks)" className={inputCls} data-testid={`${testidPrefix}-price`} />
      <textarea required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
        placeholder="Full service description — shown on the Home card and inside the detail modal."
        className={inputCls} data-testid={`${testidPrefix}-description`} />

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">Cover image (JPG/PNG/WEBP, 8MB max)</p>
          <MediaUpload
            label="image"
            accept="image/jpeg,image/png,image/webp,image/gif"
            value={form.image || ""}
            onChange={(v) => setForm({ ...form, image: v })}
            testid={`${testidPrefix}-image`}
            previewAs="image"
          />
        </div>
        <div>
          <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">Hover preview clip (MP4/WEBM, 50MB max, short loop)</p>
          <MediaUpload
            label="clip"
            accept="video/mp4,video/webm,video/quicktime"
            value={form.hover_video || ""}
            onChange={(v) => setForm({ ...form, hover_video: v })}
            testid={`${testidPrefix}-hover-video`}
            previewAs="video"
          />
        </div>
      </div>

      <div>
        <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          FAQs shown in the service detail modal
        </p>
        <FaqEditor
          faq={form.faq || []}
          onChange={(next) => setForm({ ...form, faq: next })}
          testidPrefix={testidPrefix}
        />
      </div>

      <div className="flex gap-2 pt-1">
        <button type="submit" disabled={busy} data-testid={`${testidPrefix}-save`}
          className="flex items-center gap-2 rounded-full bg-emerald-300 px-5 py-2 text-[11px] font-bold uppercase tracking-wider text-black hover:bg-emerald-200 disabled:opacity-50">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
          {isNew ? "Create Service" : "Save Changes"}
        </button>
        <button type="button" onClick={onCancel} disabled={busy} data-testid={`${testidPrefix}-cancel`}
          className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2 text-[11px] font-bold uppercase tracking-wider text-zinc-300 hover:border-emerald-300/40 hover:text-emerald-200">
          <X size={12} /> Cancel
        </button>
      </div>
    </form>
  );
}

export function ServicesTab() {
  const [services, setServices] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [busyOrder, setBusyOrder] = useState(false);

  const load = () =>
    api
      .get("/admin/services")
      .then((r) => setServices(r.data.filter((s) => s.is_active !== false)));

  useEffect(() => {
    load().catch(() => {});
  }, []);

  const commitOrder = async (next) => {
    setServices(next);
    setBusyOrder(true);
    try {
      await api.patch("/admin/services/order", { ids: next.map((s) => s.id) });
    } catch {
      toast.error("Couldn't save order — refreshing");
      load();
    } finally {
      setBusyOrder(false);
    }
  };

  const move = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= services.length) return;
    const next = [...services];
    [next[idx], next[target]] = [next[target], next[idx]];
    commitOrder(next);
  };

  const remove = async (sid) => {
    if (!window.confirm("Remove this service from the home page?")) return;
    await api.delete(`/admin/services/${sid}`);
    load();
  };

  const onSaved = () => {
    load();
    setEditingId(null);
    setAddingNew(false);
  };

  return (
    <div className="space-y-5" data-testid="admin-services-tab">
      <div className="flex items-center justify-between">
        <p className="text-xs text-zinc-500">
          These cards power the home page's Studio Services section. Reorder, rename, reprice, upload cover + hover clip, or edit FAQ.
          {busyOrder && <Loader2 size={11} className="ml-2 inline animate-spin text-emerald-300" />}
        </p>
        {!addingNew && (
          <button type="button" onClick={() => { setAddingNew(true); setEditingId(null); }}
            data-testid="admin-service-add"
            className="flex items-center gap-2 rounded-full bg-emerald-300 px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-black hover:bg-emerald-200">
            <Plus size={12} /> New Service
          </button>
        )}
      </div>

      {addingNew && (
        <ServiceForm isNew initial={EMPTY_SERVICE} onSaved={onSaved} onCancel={() => setAddingNew(false)} />
      )}

      <div className="space-y-3">
        {services.map((s, idx) => {
          const isEditing = editingId === s.id;
          return (
            <div key={s.id} data-testid={`admin-service-row-${s.id}`}>
              {isEditing ? (
                <ServiceForm initial={s} onSaved={onSaved} onCancel={() => setEditingId(null)} />
              ) : (
                <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-[#141419] p-4">
                  <span className="mt-0.5 font-mono text-[11px] uppercase text-zinc-500">{idx + 1}</span>
                  {s.image && (
                    <img src={fileUrl(s.image)} alt="" className="h-14 w-20 shrink-0 rounded-lg object-cover" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-white" data-testid={`admin-service-title-${s.id}`}>
                      {s.title}
                    </p>
                    <p className="truncate font-mono text-[11px] uppercase tracking-widest text-emerald-300">
                      {s.price_label}
                    </p>
                    <p className="mt-1 line-clamp-2 text-xs text-zinc-500">{s.description}</p>
                    <p className="mt-1 font-mono text-[10px] uppercase text-zinc-600">
                      {(s.faq || []).length} FAQ{(s.faq || []).length === 1 ? "" : "s"}
                      {s.hover_video ? " · hover clip ✓" : ""}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button type="button" onClick={() => { setEditingId(s.id); setAddingNew(false); }}
                      data-testid={`admin-service-edit-${s.id}`} aria-label="Edit service"
                      className="rounded-full border border-white/10 p-2 text-emerald-200 hover:border-emerald-300/40 hover:bg-emerald-300/10">
                      <Pencil size={13} />
                    </button>
                    <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0 || busyOrder}
                      data-testid={`admin-service-up-${s.id}`} aria-label="Move up"
                      className="rounded-full border border-white/10 p-2 text-zinc-300 hover:border-emerald-300/40 hover:bg-emerald-300/10 disabled:cursor-not-allowed disabled:opacity-30">
                      <ArrowUp size={13} />
                    </button>
                    <button type="button" onClick={() => move(idx, 1)} disabled={idx === services.length - 1 || busyOrder}
                      data-testid={`admin-service-down-${s.id}`} aria-label="Move down"
                      className="rounded-full border border-white/10 p-2 text-zinc-300 hover:border-emerald-300/40 hover:bg-emerald-300/10 disabled:cursor-not-allowed disabled:opacity-30">
                      <ArrowDown size={13} />
                    </button>
                    <button type="button" onClick={() => remove(s.id)} data-testid={`admin-service-delete-${s.id}`}
                      aria-label="Delete" className="rounded-full border border-red-500/30 p-2 text-red-400 hover:bg-red-500/10">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {services.length === 0 && !addingNew && (
          <p className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-sm text-zinc-600">
            No services yet — add your first.
          </p>
        )}
      </div>
    </div>
  );
}
